import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtCHF } from "@/lib/format";
import { Field, Sel } from "@/components/Field";
import { FormDialog, useSubmit, num } from "./FormDialog";

const CUSTOM = "__custom__";
const newLine = () => ({ key: Math.random().toString(36).slice(2), product_id: CUSTOM, name: "", unit: "pz", quantity: "", unit_cost: "", unit_price: "" });
const n = (v) => (v === "" || v == null ? null : Number(v));

const LineRow = ({ ln, i, products, onChange, onRemove }) => {
  const pick = (v) => {
    const p = products.find((x) => x.id === v);
    onChange(p ? { product_id: v, name: p.name, unit: p.unit, unit_cost: p.purchase_price ?? "", unit_price: p.sale_price ?? "" } : { product_id: CUSTOM, name: "" });
  };
  const s = (k) => (e) => onChange({ [k]: e.target.value });
  return (
    <div data-testid={`record-line-${i}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-end gap-2">
        <Field label="Prodotto" className="flex-1">
          <Sel testId={`record-line-product-${i}`} value={ln.product_id} onChange={pick} options={[{ value: CUSTOM, label: "Personalizzato" }, ...products.map((p) => ({ value: p.id, label: p.name }))]} />
        </Field>
        <Button type="button" data-testid={`record-line-remove-${i}`} variant="ghost" size="icon" className="h-11 w-11 text-red-700" onClick={onRemove} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></Button>
      </div>
      {ln.product_id === CUSTOM && (
        <div className="grid grid-cols-3 gap-2">
          <Field label="Nome *" className="col-span-2"><Input data-testid={`record-line-name-${i}`} required value={ln.name} onChange={s("name")} placeholder="Es. Pasti" className="h-11" /></Field>
          <Field label="Unità"><Input data-testid={`record-line-unit-${i}`} required value={ln.unit} onChange={s("unit")} className="h-11" /></Field>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Field label={`Quantità (${ln.unit}) *`}><Input data-testid={`record-line-qty-${i}`} type="number" min="0" step="any" required value={ln.quantity} onChange={s("quantity")} className="h-11" /></Field>
        <Field label="Costo CHF"><Input data-testid={`record-line-cost-${i}`} type="number" min="0" step="0.05" value={ln.unit_cost} onChange={s("unit_cost")} className="h-11" /></Field>
        <Field label="Prezzo CHF"><Input data-testid={`record-line-price-${i}`} type="number" min="0" step="0.05" value={ln.unit_price} onChange={s("unit_price")} className="h-11" /></Field>
      </div>
    </div>
  );
};

export const StatsRecordDialog = ({ open, onOpenChange, series, year, onSaved }) => {
  const [f, setF] = useState({ series: "", year: "", cash: "", card: "", twint: "", other_costs: "", notes: "" });
  const [lines, setLines] = useState([]);
  const [products, setProducts] = useState([]);
  const { saving, run } = useSubmit();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    api.get("/products?page_size=200").then((r) => setProducts(r.data.items)).catch(() => setProducts([]));
    const blank = { series: series || "", year: year || new Date().getFullYear() - 1, cash: "", card: "", twint: "", other_costs: "", notes: "" };
    setF(blank);
    setLines([]);
    if (series && year) {
      api.get("/stats/records", { params: { series } }).then((r) => {
        const rec = r.data.find((x) => x.year === year);
        if (!rec) return;
        setF({ ...blank, cash: rec.cash || "", card: rec.card || "", twint: rec.twint || "", other_costs: rec.other_costs || "", notes: rec.notes || "" });
        setLines(rec.lines.map((l) => ({ ...newLine(), ...l, product_id: l.product_id || CUSTOM, unit_cost: l.unit_cost ?? "", unit_price: l.unit_price ?? "" })));
      });
    }
  }, [open, series, year]);

  const patch = (key) => (p) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...p } : l)));
  const cost = lines.reduce((t, l) => t + (n(l.quantity) || 0) * (n(l.unit_cost) || 0), 0) + (n(f.other_costs) || 0);
  const income = (n(f.cash) || 0) + (n(f.card) || 0) + (n(f.twint) || 0);

  const submit = () =>
    run(async () => {
      await api.put("/stats/records", {
        series: f.series.trim(), year: Number(f.year), create: !year, cash: n(f.cash) || 0, card: n(f.card) || 0, twint: n(f.twint) || 0, other_costs: n(f.other_costs) || 0, notes: f.notes || null,
        lines: lines.map((l) => ({ product_id: l.product_id === CUSTOM ? null : l.product_id, name: l.name || "-", unit: l.unit || "pz", quantity: num(l.quantity) || 0, unit_cost: n(l.unit_cost), unit_price: n(l.unit_price) })),
      });
      onOpenChange(false);
      onSaved?.(f.series.trim());
    }, "Consuntivo salvato");

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={series && year ? `Consuntivo ${year}` : "Nuovo consuntivo"} description="Dati inseriti a mano: non modificano il magazzino." onSubmit={submit} saving={saving} testId="stats-record-dialog" wide>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Evento ricorrente *" className="col-span-2"><Input data-testid="record-series" required disabled={!!series} value={f.series} onChange={set("series")} placeholder="Es. Sagra" className="h-11" /></Field>
        <Field label="Anno *"><Input data-testid="record-year" type="number" min="1990" max="2100" required disabled={!!year} value={f.year} onChange={set("year")} className="h-11" /></Field>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Contanti CHF"><Input data-testid="record-cash" type="number" min="0" step="0.05" value={f.cash} onChange={set("cash")} className="h-11" /></Field>
        <Field label="Carte CHF"><Input data-testid="record-card" type="number" min="0" step="0.05" value={f.card} onChange={set("card")} className="h-11" /></Field>
        <Field label="TWINT CHF"><Input data-testid="record-twint" type="number" min="0" step="0.05" value={f.twint} onChange={set("twint")} className="h-11" /></Field>
        <Field label="Altre spese CHF"><Input data-testid="record-other-costs" type="number" min="0" step="0.05" value={f.other_costs} onChange={set("other_costs")} className="h-11" /></Field>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-900">Prodotti e pasti</div>
        {lines.map((l, i) => <LineRow key={l.key} ln={l} i={i} products={products} onChange={patch(l.key)} onRemove={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} />)}
        <Button type="button" data-testid="record-add-line" variant="outline" className="h-11 w-full" onClick={() => setLines((ls) => [...ls, newLine()])}><Plus className="h-4 w-4 mr-1" /> Aggiungi prodotto</Button>
      </div>
      <div data-testid="record-summary" className="rounded-lg bg-slate-50 px-3 py-2 text-sm">Incassi {fmtCHF(income)} · Costi {fmtCHF(cost)} · <strong>Saldo {fmtCHF(income - cost)}</strong></div>
      <Field label="Note"><Textarea data-testid="record-notes" value={f.notes} onChange={set("notes")} rows={2} /></Field>
    </FormDialog>
  );
};
